import { isB2BStore, Settings } from '@/data/Settings';
import { DEFAULT_CURRENCY } from '@/data/config/DEFAULTS';
import { GA4_EVENT_PAGE_LOAD, GA4_EVENT_VIEW_ITEM, NOT_APPLICABLE } from '@/data/constants/gtm';
import { PROMOTION_TYPES } from '@/data/constants/promoIconConstants';
import { ProductType, ResponseProductAttribute } from '@/data/types/Product';

import { PaymentType } from '@/data/Content/Payment';
import { PAYMENT } from '@/data/constants/order';
import { TYPES } from '@/data/constants/product';
import { dFix } from '@/data/utils/floatingPoint';
import { productIsA } from '@/utils/productIsA';
import {
	GA4_PAYMENT_TYPES,
	ONE_DAY_IN_MS,
	PREV_URL_SESSION_STORAGE_KEY,
	ITEM_DELIVERY_METHODS,
} from 'presentation/events/constants/eventConstants';
import { PromotionDetails } from 'presentation/events/hooks/useGetPromotionDetails';
import {
	getAddToCartPromotionDetailsFromSessionStorage,
	getSelectedPromotionFromSessionStorage,
} from 'presentation/events/services/promotionsService';
import {
	GA4DeliveryDaysEventParams,
	GA4EcommerceEventItemPayload,
	GA4EcommerceEventPayload,
	GA4PageLoadEvent,
	GA4PaymentType,
	GA4DeliveryDaysCheckoutEventParams,
} from 'presentation/events/types/ga4Types';
import {
	getIsBundleFromSessionStorage,
	getItemPartNumber,
	getProductPrice,
	getProductPromotions,
	getProductQuantity,
} from 'presentation/events/utils/productUtils';
import { OrderItemDeliveryDetails } from 'presentation/events/types/mediaTypes';
declare global {
	interface Window {
		dataLayer?: Record<string, unknown>[];
	}
}

export const pushToDataLayer = (data: Record<string, unknown>) => {
	if (typeof window === 'undefined') return;

	try {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push(data);
	} catch (error) {
		console.error('Error pushing event to dataLayer:', error);
	}
};

// HDM only maps up to 3 categories (can be max 5 in GA4)
const MAX_CATEGORIES = 3;

/* --------------------------------------------
 * Ecommerce Event
 * -------------------------------------------- */

export type BuildGA4EcommerceEventPayloadProps =
	| {
			event: string;
			physicalStoreId: string;
			settings: Settings;
			bundleId?: string;
			product: ProductType;
			categories: string[];
			quantity: string | number;
			promotionDetails?: PromotionDetails;
			deliveryDays?: GA4DeliveryDaysEventParams;

			items?: never;
	  }
	| {
			event: string;
			physicalStoreId: string;
			settings: Settings;
			bundleId?: string;
			items: Array<{
				product: ProductType;
				categories: string[];
				quantity: string | number;
				promotionDetails?: PromotionDetails;
			}>;
			deliveryDays?: GA4DeliveryDaysEventParams;

			product?: never;
	  };

export const buildGA4EcommerceEventPayload = (
	props: BuildGA4EcommerceEventPayloadProps
): GA4EcommerceEventPayload => {
	const { physicalStoreId, settings } = props;

	let bundleId: string | undefined = props.bundleId;
	let items: GA4EcommerceEventItemPayload[] = [];

	if (props.product) {
		// Single product

		const { product, categories, quantity, promotionDetails } = props;

		if (productIsA(product, TYPES.bundle)) {
			// Bundle

			bundleId = bundleId ?? product.partNumber;

			// TODO: bundle logic needs to be implemented
		} else {
			items = [
				buildGA4EcommerceEventItemPayload({
					product,
					categories,
					physicalStoreId,
					settings,
					quantity,
					promotionDetails,
				}),
			];
		}
	}

	if (props.items) {
		// Multiple products
		items = props.items.map((item, index) =>
			buildGA4EcommerceEventItemPayload({
				product: item.product,
				categories: item.categories,
				physicalStoreId,
				settings,
				quantity: item.quantity,
				promotionDetails: item.promotionDetails,
				index: index + 1,
				bundleId,
			})
		);
	}

	const value = dFix(
		items.reduce((value, item) => {
			const price = item.price ?? 0;
			const quantity = item.quantity ?? 0;

			return value + price * quantity;
		}, 0),
		2
	);

	const payload: GA4EcommerceEventPayload = {
		event: props.event,
		ecommerce: {
			currency: DEFAULT_CURRENCY,
			value,
			items,
		},
	};

	if (bundleId || props.event === GA4_EVENT_VIEW_ITEM) {
		payload.ecommerce.bundle_id = bundleId ?? NOT_APPLICABLE;
	}

	if (props.deliveryDays) {
		payload.ecommerce.estimated_delivery_days = props.deliveryDays.estimated_delivery_days;
		payload.ecommerce.pick_up_days = props.deliveryDays.pick_up_days;
	}

	return payload;
};

/* --------------------------------------------
 * Ecommerce Event Item
 * -------------------------------------------- */

export type BuildGA4EcommerceEventItemPayloadProps = {
	bundleId?: string;
	settings: Settings;
	product: ProductType & {
		isGift?: boolean;
		promoThdCode?: string;
	};
	categories: string[];
	physicalStoreId: string;
	quantity: string | number;
	index?: number;
	isEmarsys?: boolean;
	promotionDetails?: PromotionDetails;
	itemListId?: string;
	itemListName?: string;
	plpBadges?: string;
};

export const buildGA4EcommerceEventItemPayload = ({
	bundleId,
	product,
	categories,
	physicalStoreId,
	settings,
	index = 1,
	promotionDetails,
	itemListId,
	itemListName,
	plpBadges,
	...props
}: BuildGA4EcommerceEventItemPayloadProps): GA4EcommerceEventItemPayload => {
	const productPartNumber = getItemPartNumber(product);

	const itemAffiliation = getItemAffiliation({ settings });

	const itemCategories = getItemCategories(categories ?? []);
	const itemVariant = getItemVariant(product.attributes ?? []);

	const selectedPromotion = getSelectedPromotionFromSessionStorage(productPartNumber);

	const extendBay = isExtendedCatalog(product.attributes ?? []);

	const promotions = getProductPromotions({
		productAttributes: product.attributes ?? [],
		physicalStoreId,
	});

	const promoThd = buildItemPromoThd({ product, promotions });

	const quantity = getProductQuantity({ quantity: props.quantity });
	const { price, discount } = getProductPrice({
		product,
		promotions,
		quantity,
		promotionDetails,
	});

	return {
		// ga4 parameters
		item_id: productPartNumber,
		item_name: product.name,
		item_affiliation: itemAffiliation,
		coupon: NOT_APPLICABLE,
		discount,
		index,
		item_brand: product.manufacturer ?? NOT_APPLICABLE,
		...itemCategories,
		item_list_id: itemListId ?? NOT_APPLICABLE,
		item_list_name: itemListName ?? NOT_APPLICABLE,
		item_variant: itemVariant,
		price,
		quantity,
		creative_name: selectedPromotion.creative_name,
		creative_slot: selectedPromotion.creative_slot,
		promotion_name: selectedPromotion.promotion_name,
		promotion_id: selectedPromotion.promotion_id,

		// custom parameters
		extended_bay: extendBay,
		promo_thd: promoThd,
		bundle: bundleId !== undefined ? 'true' : 'false',
		...(plpBadges !== undefined && { plp_badges: plpBadges }),
	};
};

/* --------------------------------------------
 * Checkout Events Payload:
 * begin_checkout, add_payment_info and add_shipping_info
 * -------------------------------------------- */
type BuildGA4CheckoutEventsPayloadProps = {
	event: string;
	physicalStoreId: string;
	settings: Settings;
	items: Array<{
		product: ProductType;
		categories: string[];
		quantity: string | number;
		promotionDetails?: PromotionDetails;
		deliveryDetails?: OrderItemDeliveryDetails;
	}>;
	shippingTier?: string;
	paymentType?: PaymentType;
	tax?: number;
	shipping?: number;
	transaction_id?: string;
};
export const buildGA4CheckoutEventsPayload = (props: BuildGA4CheckoutEventsPayloadProps) => {
	const { physicalStoreId, settings } = props;

	let items: GA4EcommerceEventItemPayload[] = [];
	
	
	items = props.items.map((item, index) =>
		buildGA4CheckoutEventItemPayload({
			product: item.product,
			categories: item.categories,
			physicalStoreId,
			settings,
			quantity: item.quantity,
			promotionDetails: item.promotionDetails,
			deliveryDetails:item.deliveryDetails,
			index: index + 1,
		})
	);

	const value = dFix(
		items.reduce((value, item) => {
			const price = item.price ?? 0;
			const quantity = item.quantity ?? 0;

			return value + price * quantity;
		}, 0),
		2
	);

	const payload: GA4EcommerceEventPayload = {
		event: props.event,
		ecommerce: {
			currency: DEFAULT_CURRENCY,
			value,
			items,
		},
	};

	if (props.shippingTier) {
		payload.ecommerce.shipping_tier = props.shippingTier;
	}

	if (props.paymentType) {
		payload.ecommerce.payment_type = getGA4PaymentType(props.paymentType) ?? NOT_APPLICABLE;
	}
	if (props.tax) {
		payload.ecommerce.tax = props.tax ?? 0;
	}
	if (props.shipping) {
		payload.ecommerce.shipping = props.shipping ?? 0;
	}
	if (props.transaction_id) {
		payload.ecommerce.transaction_id = props.transaction_id ?? NOT_APPLICABLE;
	}
	return payload;
};

/* --------------------------------------------
 * Ecommerce Checkout Event Item:
 * begin_checkout, add_payment_info and add_shipping_info
 * -------------------------------------------- */

export type BuildGA4CheckoutEventItemPayloadProps = {
	settings: Settings;
	product: ProductType & {
		isGift?: boolean;
		gitfQty?: number;
		isPartOfCombo?: boolean;
		promoThdCode?: string;
	};
	categories: string[];
	physicalStoreId: string;
	quantity: string | number;
	index?: number;
	isEmarsys?: boolean;
	promotionDetails?: PromotionDetails;
	deliveryDetails?: OrderItemDeliveryDetails;
};

export const buildGA4CheckoutEventItemPayload = ({
	product,
	categories,
	physicalStoreId,
	settings,
	index = 1,
	promotionDetails,
	deliveryDetails,
	...props
}: BuildGA4CheckoutEventItemPayloadProps): GA4EcommerceEventItemPayload => {
	const productPartNumber = getItemPartNumber(product);

	const itemAffiliation = getItemAffiliation({ settings });

	const itemCategories = getItemCategories(categories ?? []);
	const itemVariant = getItemVariant(product.attributes ?? []);

	const selectedPromotion = getAddToCartPromotionDetailsFromSessionStorage(productPartNumber);

	const extendBay = isExtendedCatalog(product.attributes ?? []);

	const promotions = getProductPromotions({
		productAttributes: product.attributes ?? [],
		physicalStoreId,
	});

	if (product.isPartOfCombo) {
		promotions.push(PROMOTION_TYPES.PROMORIBBON_COMBO);
	}

	const promoThd = buildItemPromoThd({ product, promotions });

	const quantity = getProductQuantity({ quantity: props.quantity });
	const { price, discount } = getProductPrice({
		product,
		promotions,
		quantity,
		promotionDetails,
	});
	const itemDeliveryDetails = getItemDeliveryDetails(deliveryDetails);
	return {
		// ga4 parameters
		item_id: productPartNumber,
		item_name: product.name,
		item_affiliation: itemAffiliation,
		coupon: NOT_APPLICABLE,
		discount,
		index,
		item_brand: product.manufacturer ?? NOT_APPLICABLE,
		...itemCategories,
		item_list_id: NOT_APPLICABLE,
		item_list_name: NOT_APPLICABLE,
		item_variant: itemVariant,
		price,
		quantity,
		creative_name: selectedPromotion.creative_name,
		creative_slot: selectedPromotion.creative_slot,
		promotion_name: selectedPromotion.promotion_name,
		promotion_id: selectedPromotion.promotion_id,

		// custom parameters
		extended_bay: extendBay,
		promo_thd: promoThd,
		bundle: getIsBundleFromSessionStorage(productPartNumber) ? 'true' : 'false',
		...itemDeliveryDetails,
	};
};

/* --------------------------------------------
 * `page_load` event
 * -------------------------------------------- */

export type BuildGA4PageLoadEventPayloadProps = {
	categories: string[];
	sku: string;
};

export const buildGA4PageLoadEventPayload = (
	params?: BuildGA4PageLoadEventPayloadProps
): GA4PageLoadEvent => {
	if (!params) {
		return {
			event: GA4_EVENT_PAGE_LOAD,
			ecommerce: {},
		};
	}

	const { sku, categories } = params;

	const itemCategories = getItemCategories(categories ?? []);

	return {
		event: GA4_EVENT_PAGE_LOAD,
		ecommerce: {
			sku,
			...itemCategories,
		},
	};
};

/* --------------------------------------------
 * Utility
 * -------------------------------------------- */

/**
 * Get `extended_bay` property for GA4 Ecommerce event
 */
const isExtendedCatalog = (attributes: ResponseProductAttribute[]): 'true' | 'false' => {
	if (!attributes.length) return 'false';

	const extendedCatalogIcons = attributes.filter(
		(attr) =>
			attr.identifier === PROMOTION_TYPES.PROMOICON_PC ||
			attr.identifier === PROMOTION_TYPES.PROMOICON_VF ||
			attr.identifier === PROMOTION_TYPES.ONLINE_ONLY_SELLING_STORE
	);

	return extendedCatalogIcons.length > 0 ? 'true' : 'false';
};

/**
 * Get `item_variant` property for GA4 Ecommerce event
 */
const getItemVariant = (attributes: ResponseProductAttribute[]): string => {
	if (!attributes.length) return NOT_APPLICABLE;

	const colorAttribute = attributes.find(
		(attribute) =>
			attribute.usage === 'Descriptive' &&
			attribute.displayable &&
			attribute.identifier === 'COLOR' &&
			attribute.storeDisplay !== 'true'
	);

	if (!colorAttribute?.values?.length) return NOT_APPLICABLE;

	const firstValueEntry = colorAttribute.values[0];
	const { value } = firstValueEntry ?? {};

	if (Array.isArray(value)) return value.length ? value[0] : NOT_APPLICABLE;

	return value ?? NOT_APPLICABLE;
};

/**
 * Get `item_category` properties for GA4 Ecommerce event
 */
const getItemCategories = (
	categories: string[]
): {
	item_category: string;
	item_category2: string;
	item_category3: string;
} => {
	const result: {
		item_category: string;
		item_category2: string;
		item_category3: string;
	} = {
		item_category: NOT_APPLICABLE,
		item_category2: NOT_APPLICABLE,
		item_category3: NOT_APPLICABLE,
	};

	// categories normally come from the breadcrumb trail of the page
	const filteredCategories = categories.filter((category) => !category.trim().match('^SKU d+'));

	// eslint-disable-next-line functional/no-loop-statement
	for (let i = 0; i < MAX_CATEGORIES; i++) {
		const key = `item_category${i === 0 ? '' : i + 1}` as keyof typeof result;
		const category = i < filteredCategories.length ? filteredCategories[i] : undefined;

		if (category) {
			result[key] = category;
		} else {
			result[key] = NOT_APPLICABLE;
		}
	}

	return result;
};

/**
 * Get `item_affiliation` property for GA4 Ecommerce event
 */
const getItemAffiliation = ({ settings }: { settings: Settings }) => {
	const itemAffiliation = settings.userData?.DL_ITEM_AFFILIATION;
	return itemAffiliation ?? !isB2BStore(settings) ? 'The Home Depot' : 'The Home Depot PRO';
};

/**
 * Get `promo_thd` property for GA4 Ecommerce event
 */
export const buildItemPromoThd = ({
	product,
	promotions,
}: {
	product: { isGift?: boolean; promoThdCode?: string };
	promotions: string[];
}) => {
	const promoThdCode = product.promoThdCode;

	if (promoThdCode) {
		const withoutGenericFreeGift = promotions.filter(
			(promotion) => promotion !== PROMOTION_TYPES.PROMOICON_FP
		);
		return getPromoThd({ promotions: withoutGenericFreeGift, promoThdCode });
	}

	if (product.isGift) {
		promotions.push(PROMOTION_TYPES.PROMOICON_FP);
	}

	return getPromoThd({ promotions });
};

const getPromoThd = ({
	promotions,
	promoThdCode,
}: {
	promotions: string[];
	promoThdCode?: string;
}) => {
	const promotionTags = getProductPromotionTags(promotions);

	if (promoThdCode) {
		promotionTags.push(promoThdCode);
	}

	return promotionTags.length <= 0 ? NOT_APPLICABLE : promotionTags.join(',');
};

const isPROMORIBBON_NXM = (
	identifier: PROMOTION_TYPES
): identifier is PROMOTION_TYPES.PROMORIBBON_NXM_ =>
	identifier.startsWith(PROMOTION_TYPES.PROMORIBBON_NXM_);

const getProductPromotionTags = (promotions: string[]) => {
	const tags: string[] = [];
	const identifiers = promotions as PROMOTION_TYPES[];

	// eslint-disable-next-line complexity
	identifiers.forEach((identifier) => {
		switch (identifier) {
			case PROMOTION_TYPES.PROMOICON_GR:
				tags.push('R');
				break;
			case PROMOTION_TYPES.PROMORIBBON_COMBO:
				tags.push('C');
				break;
			case PROMOTION_TYPES.PROMORIBBON_AHP:
				tags.push('PM');
				break;
			case PROMOTION_TYPES.PROMOICON_FP:
				tags.push('PG');
				break;
			case PROMOTION_TYPES.PROMOICON_CP:
				tags.push('AM');
				break;
			case PROMOTION_TYPES.PROMOICON_NLP:
				tags.push('NLP');
				break;
			case PROMOTION_TYPES.ONLINE_ONLY_SELLING_STORE:
			case PROMOTION_TYPES.PROMOICON_CE:
			case PROMOTION_TYPES.PROMOICON_PC:
			case PROMOTION_TYPES.PROMOICON_VF:
				break;
			default: {
				if (isPROMORIBBON_NXM(identifier)) {
					tags.push('NXM');
					break;
				}

				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const _exhaustiveCheck = identifier;
				break;
			}
		}
	});

	return tags;
};

// Return user date/time info
export const getUserDateTime = () => {
	const date = new Date();
	return {
		weekDay: date.getDay(),
		monthDay: date.getDate(),
		time: date.getHours(),
	};
};

// Detect the browser name
// eslint-disable-next-line complexity
export const getUserBrowser = (): string => {
	if (typeof navigator === 'undefined') return NOT_APPLICABLE;

	const ua = navigator.userAgent.toLowerCase();
	if (ua.includes('edg/')) return 'Microsoft Edge';
	if (ua.includes('opr/') || ua.includes('opera')) return 'Opera';
	if (ua.includes('firefox')) return 'Mozilla Firefox';
	if (ua.includes('chrome') && !ua.includes('edg/') && !ua.includes('opr/')) return 'Google Chrome';
	if (ua.includes('safari') && !ua.includes('chrome')) return 'Apple Safari';
	return NOT_APPLICABLE;
};

// Return if it's a mobile or Desktop
export const getUserDevice = (): 'Mobile' | 'Desktop' => {
	if (typeof navigator === 'undefined') return 'Desktop';

	const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
	return mobileRegex.test(navigator.userAgent) ? 'Mobile' : 'Desktop';
};

// Detect the user OS
export const getUserOS = (): 'Windows' | 'Android' | 'iOS' | 'MacOS' | 'Linux' | 'Bot' | 'NA' => {
	if (typeof navigator === 'undefined') return NOT_APPLICABLE;

	const ua = navigator.userAgent.toLowerCase();

	if (/bot|crawler|spider|crawling/i.test(ua)) return 'Bot';
	if (ua.includes('windows')) return 'Windows';
	if (ua.includes('android')) return 'Android';
	if (/iphone|ipad|ipod/.test(ua)) return 'iOS';
	if (ua.includes('macintosh') || ua.includes('mac os')) return 'MacOS';
	if (ua.includes('linux')) return 'Linux';

	return NOT_APPLICABLE;
};

export const validateUserId = (uId: string | number, statusLoggedIn: boolean) =>
	statusLoggedIn ? uId : undefined;

export const waitForReactToBeIdle = (): Promise<void> =>
	new Promise<void>((resolve) => {
		// First requestAnimationFrame: Waits for the current render cycle to start painting
		// This ensures React has flushed its updates to the DOM
		requestAnimationFrame(() => {
			// Second requestAnimationFrame: Waits for the paint to complete and the next frame cycle to begin
			// This ensures all visual updates have been applied to the screen
			requestAnimationFrame(() => {
				// Then wait for browser to be idle (no more pending tasks)
				if ('requestIdleCallback' in window) {
					// requestIdleCallback: Waits until the browser has no high-priority work
					// Timeout of 5000ms ensures we don't wait forever if browser stays busy
					requestIdleCallback(() => resolve(), { timeout: 5000 });
				} else {
					// Fallback for browsers without requestIdleCallback:
					// setTimeout with 0ms defers to the next task in the event loop
					setTimeout(() => resolve(), 0);
				}
			});
		});
	});

const CHECKOUT_PATHNAME = /^\/checkout(?:\/.*)?$/;
const CART_PATHNAME = /^\/cart(?:\/.*)?$/;

export const isCheckout = (pathname: string) => CHECKOUT_PATHNAME.test(pathname);
export const isCart = (pathname: string) => CART_PATHNAME.test(pathname);

// eslint-disable-next-line complexity
export const getGA4PaymentType = (paymentType: PaymentType | undefined): GA4PaymentType | null => {
	if (!paymentType) return null;

	switch (paymentType) {
		case PAYMENT.paymentMethodName.mc:
		case PAYMENT.paymentMethodName.master:
		case PAYMENT.paymentMethodName.visa:
		case PAYMENT.paymentMethodName.amex:
		case PAYMENT.paymentMethodName.carnet:
		case PAYMENT.paymentMethodName.creditAr:
			return GA4_PAYMENT_TYPES.CREDIT_CARD;

		case PAYMENT.paymentMethodName.cod:
			return GA4_PAYMENT_TYPES.PAY_IN_STORE;

		case PAYMENT.paymentMethodName.oxxo:
			return GA4_PAYMENT_TYPES.OXXO;

		case PAYMENT.paymentMethodName.openPay:
			return GA4_PAYMENT_TYPES.OPENPAY;

		case PAYMENT.paymentMethodName.paypal:
			return GA4_PAYMENT_TYPES.PAYPAL;

		case PAYMENT.paymentMethodName.giftCard:
			return GA4_PAYMENT_TYPES.GIFTCARD;

		default: {
			const _exhaustiveCheck: never = paymentType;
			return _exhaustiveCheck;
		}
	}
};

export const safeDecodeUrl = (url: string): string => {
	try {
		return decodeURI(url);
	} catch {
		return url;
	}
};

export const getPreviousUrl = (): string | null => {
	if (typeof window === 'undefined') return null;
	return window.sessionStorage.getItem(PREV_URL_SESSION_STORAGE_KEY);
};

export const setPreviousUrl = (url: string): void => {
	if (typeof window === 'undefined') return;
	window.sessionStorage.setItem(PREV_URL_SESSION_STORAGE_KEY, url);
};

export const getReferrerForTracking = (): string => {
	if (typeof window === 'undefined') return '';
	const candidate = getPreviousUrl() ?? safeDecodeUrl(document.referrer);
	const currentHref = safeDecodeUrl(window.location.href);

	return candidate && candidate !== currentHref ? candidate : '';
};

export const safeEncodeUrl = (url: string): string => {
	try {
		return encodeURI(url);
	} catch {
		return url;
	}
};

export const getDaysDifference = (
	date1: Date | undefined,
	date2: Date | undefined
): number | null => {
	if (!date1 || !date2) return null;
	return Math.round(Math.abs(date2.getTime() - date1.getTime()) / ONE_DAY_IN_MS);
};

export const buildDeliveryDaysEventParams = ({
	homeDeliveryDate,
	pickupDate,
}: {
	homeDeliveryDate?: Date;
	pickupDate?: Date;
}): GA4DeliveryDaysEventParams => {
	const currentDate = new Date();
	currentDate.setHours(0, 0, 0, 0);
	return {
		estimated_delivery_days: homeDeliveryDate
			? String(getDaysDifference(currentDate, homeDeliveryDate))
			: NOT_APPLICABLE,
		pick_up_days: pickupDate ? String(getDaysDifference(currentDate, pickupDate)) : NOT_APPLICABLE,
	};
};

export const getItemDeliveryDetails = (
	deliveryDetails: OrderItemDeliveryDetails | undefined
): GA4DeliveryDaysCheckoutEventParams => {
	return {
		delivery_method: deliveryDetails?.deliveryMethod
		, ...(deliveryDetails?.deliveryMethod === ITEM_DELIVERY_METHODS.BOPIS
			? { pick_up_days: deliveryDetails?.deliveryDays }
			: { estimated_delivery_days: deliveryDetails?.deliveryDays }),
		...(deliveryDetails?.isSameDay && { express_delivery: 'true' as const })
	};
};
